import * as React from "react"
import { X } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { LoadingSpinner } from "@/components/shared/loading-spinner"
import type { AlfredClientConfig, UpdateAlfredClientRequest } from "@/types/alfred-client"

interface AlfredClientEditPanelProps {
  client: AlfredClientConfig | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (id: number, data: UpdateAlfredClientRequest) => Promise<void>
  isSaving?: boolean
}

export function AlfredClientEditPanel({
  client,
  open,
  onOpenChange,
  onSave,
  isSaving = false,
}: AlfredClientEditPanelProps) {
  const [venueCode, setVenueCode] = React.useState("")
  const [originalVenueCode, setOriginalVenueCode] = React.useState("")
  const [venueCodeError, setVenueCodeError] = React.useState<string>()
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false)
  const firstInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (client) {
      setVenueCode(client.venueCode)
      setOriginalVenueCode(client.venueCode)
      setVenueCodeError(undefined)
    }
  }, [client])

  React.useEffect(() => {
    if (open) {
      setTimeout(() => firstInputRef.current?.focus(), 100)
    }
  }, [open])

  const hasChanges = venueCode !== originalVenueCode

  const validateVenueCode = (value: string): string | undefined =>
    value.trim() ? undefined : "Venue Code is required"

  const handleVenueCodeChange = (value: string) => {
    setVenueCode(value)
    if (venueCodeError) setVenueCodeError(validateVenueCode(value))
  }

  const handleClose = () => {
    if (hasChanges) {
      setShowConfirmDialog(true)
    } else {
      onOpenChange(false)
    }
  }

  const handleDiscard = () => {
    setVenueCode(originalVenueCode)
    setVenueCodeError(undefined)
    setShowConfirmDialog(false)
    onOpenChange(false)
  }

  const handleSave = async () => {
    if (!client) return
    const nextVenueCodeError = validateVenueCode(venueCode)
    if (nextVenueCodeError) {
      setVenueCodeError(nextVenueCodeError)
      return
    }
    await onSave(client.id, { venueCode })
    setOriginalVenueCode(venueCode)
  }

  if (!client) return null

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          className="w-full sm:w-[540px] sm:max-w-[540px] bg-card border-l border-border p-0 flex flex-col [&>button]:hidden"
        >
          <SheetHeader className="px-6 py-5 border-b border-border">
            <div className="flex items-start justify-between">
              <div>
                <SheetTitle className="text-lg font-semibold text-foreground">Edit Client</SheetTitle>
                <SheetDescription className="text-muted-foreground mt-1">
                  Update client configuration for <span className="font-mono">{client.clientId}</span>
                </SheetDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="text-muted-foreground hover:text-foreground hover:bg-secondary -mr-2 -mt-1"
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Client Information
                </h3>
                <div className="flex-1 h-px bg-border" />
              </div>

              <dl className="space-y-1">
                <dt className="text-[13px] text-muted-foreground font-medium">Client ID</dt>
                <dd className="font-mono font-medium text-foreground">{client.clientId}</dd>
              </dl>

              <div className="space-y-2">
                <Label className="text-[13px] text-muted-foreground font-medium">
                  Venue Code <span className="text-destructive">*</span>
                </Label>
                <Input
                  ref={firstInputRef}
                  value={venueCode}
                  onChange={(e) => handleVenueCodeChange(e.target.value)}
                  onBlur={() => setVenueCodeError(validateVenueCode(venueCode))}
                  placeholder="e.g., LAD"
                  className={`font-mono bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                    venueCodeError ? "border-destructive" : ""
                  }`}
                />
                {venueCodeError && <p className="text-xs text-destructive">{venueCodeError}</p>}
              </div>
            </div>
          </div>

          <SheetFooter className="px-6 py-4 border-t border-border flex-row justify-between gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSaving}
              className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !hasChanges || !!venueCodeError}
              className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
            >
              {isSaving && <LoadingSpinner size="sm" className="mr-2" />}
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              You have unsaved changes. Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setShowConfirmDialog(false)}
              className="bg-secondary border-input text-muted-foreground hover:bg-secondary/80 hover:text-foreground"
            >
              Keep Editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscard}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
